// Representation modes for the protein structure viewers, shared by the
// assistant (`ProteinStructureViewer`, 3Dmol.js) and conversations
// (`ProteinStructureArtifact`, Mol*) render paths; `applyProteinViewStyle` is
// the 3Dmol implementation and `representationsFor` in `molstarViewer.ts` the
// Mol* one. The user selects the mode live in the UI; every viewer opens in
// `DEFAULT_PROTEIN_VIEW_STYLE`.

import type { GLViewer } from "3dmol";

export const ProteinViewStyle = {
  Cartoon: "cartoon",
  Stick: "stick",
  Sphere: "sphere",
  Line: "line",
  Surface: "surface",
} as const;
export type ProteinViewStyle = (typeof ProteinViewStyle)[keyof typeof ProteinViewStyle];

export const DEFAULT_PROTEIN_VIEW_STYLE: ProteinViewStyle = ProteinViewStyle.Cartoon;

export interface ProteinViewStyleOption {
  value: ProteinViewStyle;
  label: string;
}

// Ordered for display in the mode selector.
export const PROTEIN_VIEW_STYLE_OPTIONS: readonly ProteinViewStyleOption[] = [
  { value: ProteinViewStyle.Cartoon, label: "Cartoon" },
  { value: ProteinViewStyle.Stick, label: "Stick" },
  { value: ProteinViewStyle.Sphere, label: "Spacefill" },
  { value: ProteinViewStyle.Line, label: "Wireframe" },
  { value: ProteinViewStyle.Surface, label: "Surface" },
];

// Apply a representation mode to a loaded model. Clears any prior surface first
// so switching away from Surface doesn't leave it behind. Caller is responsible
// for a preceding `addModel`; this does not re-zoom.
export function applyProteinViewStyle(
  viewer: GLViewer,
  threeDmol: typeof import("3dmol"),
  style: ProteinViewStyle
): void {
  viewer.removeAllSurfaces();
  switch (style) {
    case ProteinViewStyle.Cartoon:
      viewer.setStyle({}, { cartoon: { color: "spectrum" } });
      break;
    case ProteinViewStyle.Stick:
      viewer.setStyle({}, { stick: {} });
      break;
    case ProteinViewStyle.Sphere:
      viewer.setStyle({}, { sphere: {} });
      break;
    case ProteinViewStyle.Line:
      viewer.setStyle({}, { line: {} });
      break;
    case ProteinViewStyle.Surface:
      viewer.setStyle({}, { cartoon: { color: "spectrum" } });
      viewer.addSurface(threeDmol.SurfaceType.VDW, { opacity: 0.8 });
      break;
  }
  viewer.render();
}
